/**
 * SAM-544 (N03) — client-side mirror of the tenant/business capability
 * matrix defined in sam-lambda-functions'
 * `layers/core-layer/python/capability_matrix.py` (SAM-538, PR
 * xhinola/sam-lambda-functions#342 — open, not yet merged/deployed as of
 * this writing).
 *
 * This is intentionally a literal copy of that module's DATA (which Cognito
 * group grants which capability, at which scope) — not a reinterpretation.
 * If GROUP_CAPABILITIES changes on the backend, update it here too; there is
 * no runtime endpoint that hands the frontend its own capability list, so
 * until one exists this file is the only source of truth this app has for
 * "would the backend actually let me do this".
 *
 * MANAGE_MERCHANT changed with SAM-535 (this file used to say "do not build a
 * merchant editor"). It is now granted to one group, `xhinola_admin`, and
 * `portalApi` exposes list/upsert/deactivate actions for
 * `sam_core.merchant_config`.
 *
 * The group is the whole check on the backend too, now that
 * POST /business_access/users refuses to assign it (an earlier version also
 * required a dedicated Cognito pool, because that endpoint would hand any named
 * group to anyone with a business API key).
 *
 * This still is not a security boundary. Nothing decided in a browser is: the
 * token is what the server reads, and it re-checks the group itself. Treat
 * anything here as a decision about what to SHOW.
 *
 * Credential material never reaches this app: a merchant_config row NAMES a
 * Secrets Manager secret, and the endpoint neither accepts nor returns the
 * secret's contents. Do not add fields for an API key or shared secret.
 *
 * One important gap remains, by design (do not "fix" it client-side):
 *   - MANAGE_PERMISSIONS is defined here (mirrors the backend constant) but
 *     no endpoint enforces or grants it yet — assigning capabilities to a
 *     user is not possible through the API today. Treat it as reserved.
 *
 * Fail closed: an unrecognized group, a missing token, or a capability with
 * no matching group all resolve to "not granted" — never to "granted by
 * default". This mirrors capability_matrix.check_capability's own default.
 */

export const MANAGE_MERCHANT = 'manage_merchant';
export const MANAGE_SERVICES = 'manage_services';
export const MANAGE_BOOKINGS = 'manage_bookings';
export const MANAGE_PAYMENTS = 'manage_payments';
export const VOID_PAYMENT = 'void_payment';
export const REFUND_PAYMENT = 'refund_payment';
export const MANAGE_PERMISSIONS = 'manage_permissions';
/** Cognito user administration and reception's verified-document control (samUserManagement). */
export const MANAGE_USERS = 'manage_users';

export type Capability =
  | typeof MANAGE_MERCHANT
  | typeof MANAGE_SERVICES
  | typeof MANAGE_BOOKINGS
  | typeof MANAGE_PAYMENTS
  | typeof VOID_PAYMENT
  | typeof REFUND_PAYMENT
  | typeof MANAGE_PERMISSIONS
  | typeof MANAGE_USERS;

export const SCOPE_TENANT = 'tenant' as const;
export const SCOPE_BUSINESS = 'business' as const;
export type CapabilityScope = typeof SCOPE_TENANT | typeof SCOPE_BUSINESS;

const SCOPE_RANK: Record<CapabilityScope, number> = {
  [SCOPE_BUSINESS]: 1,
  [SCOPE_TENANT]: 2,
};

/**
 * group name (Cognito `cognito:groups` entry) -> { capability: scope }
 *
 * NOTE ON NAMING: these are the two literal Cognito groups
 * `capability_matrix.py` checks — 'admin' (tenant-wide) and 'business_admin'
 * (scoped to the caller's own business). They are NOT the same thing as
 * this admin app's own feature-gating groups (`business_payments`,
 * `business_services`, etc. — see REQUIRED_GROUPS in
 * src/pages/settings/PermissionGroups.tsx), even though the string
 * 'business_admin' unfortunately collides between the two systems: in
 * PermissionGroups.tsx it means "full access to this admin panel"; here (as
 * defined by the backend) it means "business-scoped capability grant, not
 * tenant-wide". A user only gets the capabilities below if THIS module's
 * groups are present on their JWT — having the admin-panel's own
 * `business_admin` feature group does not by itself imply the backend will
 * grant manage_services/void_payment/etc. See PermissionGroups.tsx for the
 * documented consequence.
 */
export const GROUP_CAPABILITIES: Record<string, Partial<Record<Capability, CapabilityScope>>> = {
  admin: {
    [MANAGE_SERVICES]: SCOPE_TENANT,
    [MANAGE_BOOKINGS]: SCOPE_TENANT,
    [MANAGE_PAYMENTS]: SCOPE_TENANT,
    [VOID_PAYMENT]: SCOPE_TENANT,
    [REFUND_PAYMENT]: SCOPE_TENANT,
    [MANAGE_PERMISSIONS]: SCOPE_TENANT,
    [MANAGE_USERS]: SCOPE_TENANT,
    // MANAGE_MERCHANT intentionally absent — see module docstring.
  },
  business_admin: {
    [MANAGE_SERVICES]: SCOPE_BUSINESS,
    [MANAGE_BOOKINGS]: SCOPE_BUSINESS,
    [MANAGE_PAYMENTS]: SCOPE_BUSINESS,
    [VOID_PAYMENT]: SCOPE_BUSINESS,
    [REFUND_PAYMENT]: SCOPE_BUSINESS,
    [MANAGE_USERS]: SCOPE_BUSINESS,
    // MANAGE_PERMISSIONS intentionally absent.
  },
  // The user / role manager groups (capability_matrix.py). Business-scoped MANAGE_USERS only.
  business_users: {
    [MANAGE_USERS]: SCOPE_BUSINESS,
  },
  business_roles: {
    [MANAGE_USERS]: SCOPE_BUSINESS,
  },
  // SAM-535. An ordinary group in the ordinary pools. Granted only with AWS
  // access — POST /business_access/users refuses to assign it.
  xhinola_admin: {
    [MANAGE_MERCHANT]: SCOPE_TENANT,
  },
};

/** The widest scope any of the caller's groups grant for `capability`, or null. */
export function capabilityScope(groups: string[], capability: Capability): CapabilityScope | null {
  let best: CapabilityScope | null = null;
  for (const group of groups) {
    const scope = GROUP_CAPABILITIES[group]?.[capability];
    if (scope && (best === null || SCOPE_RANK[scope] > SCOPE_RANK[best])) {
      best = scope;
    }
  }
  return best;
}

/**
 * Mirrors capability_matrix.check_capability: true only when a group grants
 * the capability AND (it's tenant-wide) OR (it's business-scoped and
 * `targetBusinessId` is undefined or equal to the caller's own business).
 * Fails closed on any ambiguity — e.g. a business-scoped grant with no known
 * caller business id is denied, never silently allowed.
 */
export function hasCapability(
  groups: string[],
  capability: Capability,
  callerBusinessId: string | null | undefined,
  targetBusinessId?: string | null,
): boolean {
  const scope = capabilityScope(groups, capability);
  if (scope === null) return false;
  if (scope === SCOPE_TENANT) return true;

  // SCOPE_BUSINESS
  if (!callerBusinessId) return false;
  if (targetBusinessId != null && String(targetBusinessId) !== String(callerBusinessId)) {
    return false;
  }
  return true;
}
